import React, { Component } from 'react';
import './SavedValuationsPage.css';
import ApiHandler from "src/api/ApiHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import ErrorHandler from "src/components/errors/ErrorHandler"
import Loader from "src/components/general/Loading"
import LoginHelper from "src/components/login/LoginHelper"
import RedirectToLoginException from "src/validation/exception_messages/RedirectToLoginException"
import StateConstants from "src/states/StateContstants"
import SaveValuationButton from "src/components/general/SaveValuationButton"
import UserVisibleErrorStoringDecorator from '../decorators/UserErrorStoringDecorator';
import navigationService from "src/navigation/NavigationService";
import AddressThumbnail from "src/components/address_images/AddressThumbnail";
import PropertyFeatures from "src/components/general/PropertyFeatures";

interface SavedValuationsPageProps {
    apiHandler: ApiHandler;
    stateHandler: EventAdaptingStateHandler;
    errorHandler: ErrorHandler;
    loader: Loader;
    loginHelper: LoginHelper;
}

interface SavedValuationsPageState {
    retrievedValuations: boolean;
    isLoggedIn: boolean;
    isLoading: boolean;
    error: string | null;
    deleteError: string | null;
    hasMadeQuery: boolean;
}

class SavedValuationsPage extends Component<SavedValuationsPageProps, SavedValuationsPageState> {

    stateHandler: EventAdaptingStateHandler;

    constructor(props: SavedValuationsPageProps) {
        super(props);
        this.stateHandler = props.stateHandler;
        this.getContent = this.getContent.bind(this);
        this.state = {
            retrievedValuations: false,
            isLoggedIn: false,
            isLoading: true,
            error: null,
            deleteError: null,
            hasMadeQuery: false,
        };
        this.props.stateHandler.addListener(StateConstants.IS_LOGGED_IN, (state: any) => {
            this.setState({
                isLoggedIn: state,
            });
        });
        this.props.stateHandler.addListener(StateConstants.SAVED_VALUATIONS, (state: any) => {
            this.setState({
                retrievedValuations: true,
            });
        });
        this.props.stateHandler.addListener(StateConstants.ERROR, (state: any) => {
            this.setState({
                error: state,
            });
        });
        this.props.stateHandler.addListener(StateConstants.IS_LOADING, (state: any) => {
            this.setState({
                isLoading: state,
            });
        });
    }

    componentDidMount() {
        if (this.state.hasMadeQuery) {
            return;
        }
        this.setState({hasMadeQuery: true});
        this.loadSavedValuations();
    }

    @UserVisibleErrorStoringDecorator(StateConstants.IS_LOADING)
    async loadSavedValuations() {
        const output = await this.props.apiHandler.listValuations({
            limit_per_page: 50,
            include_items: true
        });
        this.stateHandler.set(StateConstants.SAVED_VALUATIONS, output);
    }

    getContent() {
        let error = this.stateHandler.get(StateConstants.ERROR);
        if (error) {
            return this.props.errorHandler.render(error, {
                onDismiss: () => this.props.stateHandler.set(StateConstants.ERROR, null)
            });
        }
        let isLoading = this.stateHandler.get(StateConstants.IS_LOADING);
        if (isLoading) {
            return (
                <div className="loading-container">
                    {this.props.loader.render("", "saved-valuations")}
                </div>
            );
        }
        let savedValuations = this.stateHandler.get(StateConstants.SAVED_VALUATIONS);
        if (!savedValuations || savedValuations["i"].length === 0) {
            return (
                <div className="empty-state">
                    <div className="empty-icon">📋</div>
                    <h2>No Saved Homes</h2>
                    <p>You haven't saved any valuations yet.</p>
                    <a href="/" className="start-valuing-button">
                        Start Valuing Properties
                    </a>
                </div>
            );
        } 
        return (
                <div className="valuations-list">
                    {savedValuations["i"].map((valuation: any) => (
                        <SavedValuationCard apiHandler={this.props.apiHandler} stateHandler={this.props.stateHandler} loginHelper={this.props.loginHelper} valuation={valuation} />
                    ))}
                </div>
            );
    }


    render() {
        const content = this.getContent();
        return (
            <div className="saved-valuations-page">
                <div className="page-header">
                    <h1>Saved Homes</h1>
                    <p className="page-description">
                        View and manage your saved property valuations
                    </p>
                </div>
                {content}
            </div>
        );
    }
}


interface SavedValuationCardProps {
    apiHandler: ApiHandler;
    stateHandler: EventAdaptingStateHandler;
    loginHelper: LoginHelper;
    valuation: any;
}


class SavedValuationCard extends Component<any> {
    constructor(props: any) {
        super(props);
    }

    navigateToValuation(valuation: any) {
        let removedHomes: string | undefined = undefined;
        if ("rn" in valuation && valuation.rn.length > 0) {
            removedHomes = valuation.rn.join(",");
        }
        let relativeCondition = valuation.a.rc?.toString();
        navigationService.navigateToValuation(
            valuation.a.id,
            this.props.apiHandler,
            relativeCondition,
            removedHomes
        );
    }

    handleCardClick = (e: React.MouseEvent) => {
        const target = e.target as HTMLElement;
        if (target.closest('.valuation-card-actions')) {
            return;
        }
        this.navigateToValuation(this.props.valuation);
    }

    render() {
        const { valuation } = this.props;
        const addressId = valuation?.a?.id;
        const addressString = valuation?.a?.a ?? "";
        return (
            <div className="valuation-card" onClick={this.handleCardClick}>
                {addressId && addressString && this.props.apiHandler && (
                    <div className="valuation-card-thumbnail-wrap">
                        <AddressThumbnail
                            locationId={addressId}
                            locationString={addressString}
                            apiHandler={this.props.apiHandler}
                        />
                    </div>
                )}
                <div className="valuation-card-content">
                    <h3 className="address-title">{valuation.a.a}</h3>
                    <PropertyFeatures features={valuation.a.af} />
                    <div className="valuation-card-actions">
                        <SaveValuationButton
                            apiHandler={this.props.apiHandler}
                            stateHandler={this.props.stateHandler}
                            loginHelper={this.props.loginHelper}
                            valuationId={valuation.id}
                            isSingleValuationPage={false}
                        />
                    </div>
                </div>
            </div>
        );
    }
}

export default SavedValuationsPage;
